import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/combineLatest';
import { ActivatedRoute } from '@angular/router';

import { EntityType, RecipeComponentStates, OrderFields } from '../dictionaries';
import {
  AddToMenuShowModalObj,
  AlternativeProduct,
  Configuration,
  FoodRequestOptions,
  ImageInfo,
  Recipe,
  RecipeComponentRequest,
  RecipeFilter,
  RecipeIngredient, RecipeIngredientData,
  RecipeRequest,
  RecipeStep,
  RecipeStepData,
  RecipeStepRequest,
  ServerResponse, UserCredentials
} from '../models';
import { AccountService } from './account.service';
import { AppConfig } from './app.config';
import { FilesService } from './files.service';
import { RestService } from './rest.service';
import { CookbookService } from './cookbook.service';
import {CookieService} from '@app/shared/services/cookie.service';
import {SelectItem} from 'primeng/api';
import {ChangeFiltersService} from "@app/shared/services/change-filters.service";

@Injectable()
export class RecipeService {
  refreshShortTimeout = null;
  refreshTimeout = null;

  constructor(
    private restService: RestService,
    private accountService: AccountService,
    private filesService: FilesService,
    private cookbookService: CookbookService,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    public changeFiltersService: ChangeFiltersService,
  ) {
    this.Config = AppConfig.config;

    this.accountService.getLogOutSubscribtion().subscribe(x => {
      if (x) {
        this.clearData();
      }
    });
  }

  private Config: Configuration;

  private clearFilterState = new BehaviorSubject<number>(null);
  private totalRecipes: number = null;
  private totalRecipesSubject = new BehaviorSubject<number>(null);
  private requestOptions = new RecipeFilter();
  private recipesSubject = new BehaviorSubject<Recipe[]>(null);
  private recipes: Recipe[];
  private isLoading = false;
  private isShortLoading = false;
  private isFilterChanged = true;
  private isShortFilterChanged = true;
  private numberOfPages = null;
  private numberOfPagesSubject = new BehaviorSubject<number>(null);
  private currentPage = null;
  private currentPageSubject = new BehaviorSubject<number>(null);
  private insertStageSubject = new BehaviorSubject<string>(null);
  private updateSingleRecipeSubject = new BehaviorSubject<any>(null);
  private shortRequestOptions = new RecipeFilter();
  private shortRecipesSubject = new BehaviorSubject<Recipe[]>(null);
  private shortRecipes: Recipe[];
  private numberOfShortPages = null;
  private numberOfShortPagesSubject = new BehaviorSubject<number>(null);
  private currentShortPage = null;
  private currentShortPageSubject = new BehaviorSubject<number>(null);
  private recipesSteps: RecipeStep[];
  private recipesStepsSubject = new BehaviorSubject<RecipeStep[]>(null);
  private orderFields: SelectItem[] = OrderFields.fields;
  private progressHash = [];
  private isSortActive = new BehaviorSubject<boolean>(false);
  private searchStatus = new BehaviorSubject<boolean>(false);
  private showModalAddToMenu = new BehaviorSubject<AddToMenuShowModalObj>({
    recipeId: 0,
    stageId: 0,
    calculationWeight: 0,
    isModified: false,
    portion: 0,
    typeMenu: 0,
    singleNPart: 0
  });

  public sortHashObs = new BehaviorSubject(null);
  public categoryParentNameHash;
  public categoryNameHash;
  public subCategoryNameHash;
  public categoryTypeHash;
  public productsIdHash;
  public booksIdHash = [];
  public searchItem;
  public queryParamsMap;
  public selectedField;
  public sortHashDirection;
  public hashFiltered = new BehaviorSubject(true);

  private refresh(page = 1): void {
    const pageSize = page * 10;
    this.accountService.getCredentialsPromise().then(cred => {
      this.requestOptions.Id_session = cred.Id_Session;
      this.requestOptions.Id_app = this.Config.AppId;
      this.requestOptions.ManualJSON = 0;
      this.requestOptions.PageSize = pageSize;
      this.requestOptions.Page = this.requestOptions.Page || 1;
      this.requestOptions.Controller0 = 'User';
      this.requestOptions.EntityId0 = cred.Id_User;
      this.requestOptions.Section = 'Recipe';
      this.requestOptions.Method = 'Print';
      this.requestOptions.IsShort = 1;
      this.requestOptions.ApiVersion = 'v1';
      this.requestOptions.Sort = this.requestOptions.Sort || 'Id_Recipe desc';
      this.requestOptions.IsExcluded = this.requestOptions.IsExcluded ? this.requestOptions.IsExcluded : 0;

      this.currentPage = this.requestOptions.Page;
      this.currentPageSubject.next(this.currentPage);

      this.changeFiltersService.getPromiseStateReadyMenu().then( (x: string[]) => {
        this.requestOptions.SectionString = x.toString();
        if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(() => {
          this.refreshRecipes();
        }, 250);
      });
    });
  }

  public getTotalRecipes(): Observable<number> {
    if (this.totalRecipes === null) {
      this.accountService.getCredentialsPromise().then(cred => {

        const request = new RecipeFilter();
        request.Id_session = cred.Id_Session;
        request.Id_app = this.Config.AppId;
        request.ApiVersion = 'v1';
        request.ManualJSON = 0;
        request.Controller0 = 'Stat';
        request.EntityId0 = 3;
        request.Method = 'Print';
        this.restService.get(request).then(x => {
          if (x !== null) {
            const response: ServerResponse<string> = x;
            if (response.Result && response.Result.Resultset && response.Result.Resultset[0]) {
              this.totalRecipes = response.Result.Resultset[0]['KPI_Value'];
              this.totalRecipesSubject.next(this.totalRecipes);
            }
          }
        });
      });
    }
    return this.totalRecipesSubject.asObservable();
  }

  private refreshShort(): void {

    if (!this.isShortFilterChanged && this.shortRecipes && this.shortRecipes.length) {
      this.shortRecipesSubject.next(this.shortRecipes);
      return;
    }

    this.accountService.getCredentialsPromise().then(cred => {
      this.shortRequestOptions.Id_session = cred.Id_Session;
      this.shortRequestOptions.Id_app = this.Config.AppId;
      this.shortRequestOptions.ManualJSON = 0;
      this.shortRequestOptions.PageSize = 5;
      this.shortRequestOptions.IsShort = 1;
      this.shortRequestOptions.IsExcluded = 0;
      this.shortRequestOptions.Page = this.shortRequestOptions.Page || 1;
      this.shortRequestOptions.Controller0 = 'User';
      this.shortRequestOptions.EntityId0 = cred.Id_User;
      this.shortRequestOptions.Section = 'Recipe';
      this.shortRequestOptions.Method = 'Print';
      this.shortRequestOptions.ApiVersion = 'v1';
      this.shortRequestOptions.Sort = this.shortRequestOptions.Sort || 'Id_Recipe desc';
      this.shortRequestOptions.IsExcluded = this.shortRequestOptions.IsExcluded ? this.shortRequestOptions.IsExcluded : 0;

      this.currentShortPage = this.shortRequestOptions.Page;
      this.currentShortPageSubject.next(this.currentShortPage);

      this.changeFiltersService.getPromiseStateReadyMenu().then( (x: string[]) => {
        this.shortRequestOptions.SectionString = x.toString();
        if (this.refreshShortTimeout) clearTimeout(this.refreshShortTimeout);
        this.refreshShortTimeout = setTimeout(() => {
          this.refreshShotRecipes();
        }, 250);
      });
    });

  }

  public getSearchStatus(): Observable<boolean> {
    return this.searchStatus.asObservable();
  }

  public setSearchStatus(status: boolean) {
    this.searchStatus.next(status);
  }

  public getStatusActivatingSort(): Observable<boolean> {
    return this.isSortActive.asObservable();
  }

  public activateSort() {
    this.isSortActive.next(true);
  }

  public deactivateSort() {
    this.setFilter('Sort', undefined);
    this.isSortActive.next(false);
  }

  public getNumberOfPages(): Observable<number> {
    return this.numberOfPagesSubject.asObservable();
  }

  public getNumberOfShortPages(): Observable<number> {
    return this.numberOfShortPagesSubject.asObservable();
  }

  public refreshNumberOfShortPages() {
    this.numberOfShortPagesSubject.next(this.numberOfShortPages);
  }

  public get(forceUpdate = false, page = 1): Observable<Recipe[]> {
    if (!this.recipes || this.isFilterChanged || forceUpdate) {
      this.isFilterChanged = true;
      this.refresh(page);
      this.isFilterChanged = false;
    }

    return this.recipesSubject.asObservable();
  }

  public clearScrollRecipes(isTable): Promise<boolean>  {
    if (!isTable && (!this.recipes || this.isFilterChanged)) {
      this.cookieService.clearScrollPosition();
      return new Promise<boolean>(resolve => {
        resolve(true);
      });
    }
    return new Promise<boolean>(resolve => {
      resolve(false);
    });
  }

  public getPage(page: number) {
    if (page <= this.numberOfPages && page !== this.requestOptions.Page) {
      this.requestOptions.Page = page;
      this.isFilterChanged = true;
    }
  }

  public getShort(forceUpdate = false): Observable<Recipe[]> {
    if (!this.shortRecipes || this.isShortFilterChanged || forceUpdate) {
      this.isShortFilterChanged = true;
      this.refreshShort();
      this.isShortFilterChanged = false;
    }
    return this.shortRecipesSubject.asObservable();
  }

  public getShortPage(page: number) {
    if (page <= this.numberOfShortPages && page !== this.shortRequestOptions.Page) {
      this.shortRequestOptions.Page = page;
      this.isShortFilterChanged = true;
    }
  }

  public getCurrentPage(): Observable<number> {
    return this.currentPageSubject.asObservable();
  }

  public getCurrentShortPage(): Observable<number> {
    return this.currentShortPageSubject.asObservable();
  }

  public refreshCurrentShortPage() {
    this.currentShortPageSubject.next(this.currentShortPage);
  }

  public getSingleRecipePlanning(id_Plan: number, id_Stage: number): Promise<Recipe> {
    return new Promise<Recipe>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const requestOptions = new FoodRequestOptions();

        requestOptions.ApiVersion = 'v1';
        requestOptions.Id_session = cred.Id_Session;
        requestOptions.Controller0 = 'Plans';
        requestOptions.EntityId0 = id_Plan;
        requestOptions.Controller1 = 'Stage';
        requestOptions.EntityId1 = id_Stage;
        requestOptions.Controller2 = undefined;
        requestOptions.EntityId2 = undefined;
        requestOptions.Section = undefined;
        requestOptions.Method = 'print';
        requestOptions.Id_app = this.Config.AppId;
        requestOptions.ManualJSON = 1;

        this.restService.get(
          requestOptions).then(x => {
          const response: ServerResponse<Recipe> = x;
          resolve(response.Result.Resultset[0]);
        });
      });
    });
  }

  public getSingleRecipe(id: number): Promise<Recipe> {
    return new Promise<Recipe>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const requestOptions = new FoodRequestOptions();

        requestOptions.Id_session = cred.Id_Session;
        requestOptions.Id_app = this.Config.AppId;
        requestOptions.ManualJSON = 1;
        requestOptions.Controller0 = 'User';
        requestOptions.EntityId0 = cred.Id_User;
        requestOptions.Controller1 = 'Recipe';
        requestOptions.EntityId1 = id;
        requestOptions.Method = 'Print';
        requestOptions.ApiVersion = 'v1';
        requestOptions.qfiltr = this.requestOptions.QFiltr;
        requestOptions.sectionstring = this.requestOptions.SectionString;

        this.restService.get(
          requestOptions).then(x => {
            const response: ServerResponse<Recipe> = x;
            resolve(response.Result.Resultset[0]);
          });
      });
    });
  }

  public createSingleRecipePromise(newFullName: string, newDescription: string,
    id_newCategory: number, newRecipeWeight: number) {
    return new Promise<any>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const recipeUpdateRequest = new RecipeRequest();
        recipeUpdateRequest.Description = newDescription;
        recipeUpdateRequest.FullName = newFullName;
        recipeUpdateRequest.Id_Category = id_newCategory;
        recipeUpdateRequest.Id_Owner = cred.Id_User;
        recipeUpdateRequest.CalculationWeight = newRecipeWeight;
        recipeUpdateRequest.Id_session = cred.Id_Session;
        recipeUpdateRequest.Id_app = 3;
        recipeUpdateRequest.Controller0 = 'Recipe';
        recipeUpdateRequest.Method = 'insert';
        recipeUpdateRequest.ApiVersion = 'v1';

        this.restService.post(recipeUpdateRequest).then(x => {
          resolve(x);
        });
      });
    });


  }

  public createCopyOfSingleRecipe(idOfRecipeForCopy: number) {

    this.accountService.getCredentialsPromise().then(cred => {
      const recipeUpdateRequest = new RecipeRequest();
      recipeUpdateRequest.Id_Recipe = idOfRecipeForCopy;
      recipeUpdateRequest.Id_session = cred.Id_Session;
      recipeUpdateRequest.Id_app = 3;
      recipeUpdateRequest.Controller0 = 'Recipe';
      recipeUpdateRequest.Method = 'insert';
      recipeUpdateRequest.ApiVersion = 'v1';

      this.restService.post(recipeUpdateRequest).then(x => {
        this.updateSingleRecipeSubject.next(x);
      });
    });

    return this.updateSingleRecipeSubject.asObservable();
  }

  public deleteRecipe(id_recipeForDelete: number): Promise<ServerResponse<any>> {
    return new Promise<any>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const recipeUpdateRequest = new RecipeRequest();
        recipeUpdateRequest.Id_session = cred.Id_Session;
        recipeUpdateRequest.Id_app = 3;
        recipeUpdateRequest.Controller0 = 'Recipe';
        recipeUpdateRequest.EntityId0 = id_recipeForDelete;
        recipeUpdateRequest.Method = 'delete';
        recipeUpdateRequest.ApiVersion = 'v1';

        this.restService.post(recipeUpdateRequest).then(x => {
          if (x !== null) {
            this.refresh();
            this.refreshShort();
            resolve(x);
          }
        });
      });
    });
  }

  public createNewRecipe(currentRecipe: Recipe, newRecipeComponents: RecipeIngredient[],
    newRecipeSteps: RecipeStep[], newRecipeStepsImages: ImageInfo[]): Promise<number> {
    if ((newRecipeComponents && newRecipeComponents.length)) {
      return new Promise(resolve => {
        const promises: Promise<any>[] = [];
        let newRecipeId: number;
        this.createSingleRecipePromise(currentRecipe.FullName, currentRecipe.Description,
          currentRecipe.Id_Category, currentRecipe.CalculationWeight).then(result => {
            const response: ServerResponse<string> = result;
            if (response.Result.Id_Recipe !== undefined) {
              newRecipeId = response.Result.Id_Recipe;
              currentRecipe.Id_Recipe = newRecipeId;
              if (currentRecipe.Image) {
                promises.push(new Promise<null>(res => {
                  this.filesService.uploadImage(null, EntityType.Recipes, newRecipeId, currentRecipe.Image, currentRecipe.ImageSize)
                    .then(resp => {
                      if (resp) {
                        currentRecipe.ImagePath = resp;
                        currentRecipe.Image = undefined;
                        currentRecipe.ImageFile = undefined;
                      }
                      this.updateMainInfoImage(currentRecipe).then(() => {
                        res();
                      });
                    }, () => {
                      res();
                    });
                }));
              }
              if (newRecipeSteps && newRecipeSteps.length) {
                promises.push(this.updateAllSteps(newRecipeSteps, response.Result.Id_Recipe, newRecipeStepsImages));
              }
              promises.push(this.updateAllComponents(newRecipeComponents, response.Result.Id_Recipe));
              Promise.all(promises).then(resultIndexContainer => {
                this.refresh();
                this.refreshShort();
                newRecipeComponents = [];
                newRecipeSteps = [];
                resolve(newRecipeId);
              });
            }
          });
      });
    }

    return null;
  }

  public updateMainInfo(currentRecipe: Recipe): Promise<Recipe> {
    return new Promise<Recipe>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const recipeUpdateRequest = new RecipeRequest();
        recipeUpdateRequest.CalculationWeight = currentRecipe.CalculationWeight;
        recipeUpdateRequest.Description = currentRecipe.Description;
        recipeUpdateRequest.FullName = currentRecipe.FullName;
        recipeUpdateRequest.Id_Category = currentRecipe.Id_Category;
        recipeUpdateRequest.ImagePath = currentRecipe.ImagePath;
        recipeUpdateRequest.Id_session = cred.Id_Session;
        recipeUpdateRequest.Id_app = 3;
        recipeUpdateRequest.Controller0 = 'Recipe';
        recipeUpdateRequest.EntityId0 = currentRecipe.Id_Recipe;
        recipeUpdateRequest.Method = 'update';
        recipeUpdateRequest.ApiVersion = 'v1';

        this.filesService.uploadImage(null, EntityType.Recipes, currentRecipe.Id_Recipe, currentRecipe.Image, currentRecipe.ImageSize)
          .then(resp => {
            if (resp) {
              recipeUpdateRequest.ImagePath = resp;
              currentRecipe.Image = undefined;
              currentRecipe.ImagePath = resp;
            }

            this.restService.post(recipeUpdateRequest).then(x => {
              resolve(currentRecipe);
            });
          }, () => {
            recipeUpdateRequest.ImagePath = undefined;
            this.restService.post(recipeUpdateRequest).then(x => {
              currentRecipe.ImagePath = undefined;
              resolve(currentRecipe);
            });
          });
      });
    });
  }

  public updateMainInfoImage(currentRecipe: Recipe): Promise<Recipe> {
    return new Promise<Recipe>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const recipeUpdateRequest = new RecipeRequest();
        recipeUpdateRequest.FullName = currentRecipe.FullName;
        recipeUpdateRequest.ImagePath = currentRecipe.ImagePath;
        recipeUpdateRequest.Id_session = cred.Id_Session;
        recipeUpdateRequest.Id_app = 3;
        recipeUpdateRequest.Controller0 = 'Recipe';
        recipeUpdateRequest.EntityId0 = currentRecipe.Id_Recipe;
        recipeUpdateRequest.Method = 'update';
        recipeUpdateRequest.ApiVersion = 'v1';

        this.filesService.uploadImage(null, EntityType.Recipes, currentRecipe.Id_Recipe, currentRecipe.Image, currentRecipe.ImageSize)
          .then(resp => {
            if (resp) {
              recipeUpdateRequest.ImagePath = resp;
              currentRecipe.Image = undefined;
              currentRecipe.ImagePath = resp;
            }

            this.restService.post(recipeUpdateRequest).then(x => {
              resolve(currentRecipe);
            });
          }, () => {
            recipeUpdateRequest.ImagePath = undefined;
            this.restService.post(recipeUpdateRequest).then(x => {
              currentRecipe.ImagePath = undefined;
              resolve(currentRecipe);
            });
          });
      });
    });
  }

  public getSingleRecipeComponents(recipeId: number): Promise<RecipeIngredientData[]> {
    return new Promise<RecipeIngredientData[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const requestOptions = new FoodRequestOptions();
        requestOptions.Id_session = cred.Id_Session;
        requestOptions.Id_app = this.Config.AppId;
        requestOptions.ManualJSON = 0;
        requestOptions.Controller0 = 'Recipe';
        requestOptions.EntityId0 = recipeId;
        requestOptions.Section = 'Compaund';
        requestOptions.Method = 'Print';
        requestOptions.ApiVersion = 'v1';

        this.restService.get(
          requestOptions).then(x => {
            const response: ServerResponse<RecipeIngredientData> = x;
            resolve(response.Result.Resultset);
          });
      });
    });
  }

  public getSingleComponentAlternative(idOfRecipe: number, idOfComponent: number) {
    return new Promise<AlternativeProduct[]>(res => {
      const requestOptions = new FoodRequestOptions();

      this.accountService.getCredentialsPromise().then(cred => {
        requestOptions.Id_session = cred.Id_Session;
        requestOptions.Id_app = this.Config.AppId;
        requestOptions.ManualJSON = 0;
        requestOptions.Controller0 = 'Recipe';
        requestOptions.EntityId0 = idOfRecipe;
        requestOptions.Controller1 = 'Product';
        requestOptions.EntityId1 = idOfComponent;
        requestOptions.Section = 'Alternative';
        requestOptions.Method = 'Print';
        requestOptions.ApiVersion = 'v1';

        this.restService.get(
          requestOptions).then(x => {
            if (x !== null) {
              const response: ServerResponse<AlternativeProduct> = x;

              res(response.Result.Resultset);
            }
          });
      });

    });
  }

  public getSingleRecipeSteps(id: number): Observable<RecipeStep[]> {

    this.accountService.getCredentialsPromise().then(cred => {
      const requestOptions = new FoodRequestOptions();

      requestOptions.Id_session = cred.Id_Session;
      requestOptions.Id_app = this.Config.AppId;
      requestOptions.ManualJSON = 0;
      requestOptions.Controller0 = 'Recipe';
      requestOptions.EntityId0 = id;
      requestOptions.Section = 'Stage';
      requestOptions.Method = 'Print';
      requestOptions.ApiVersion = 'v1';

      this.restService.get(
        requestOptions).then(x => {
          if (x !== null) {
            const response: ServerResponse<RecipeStep> = x;
            this.recipesSteps = response.Result.Resultset;
            this.recipesStepsSubject.next(this.recipesSteps);
          }
        });
    });

    return this.recipesStepsSubject.asObservable();
  }

  public getSingleRecipeStepsPrint(id: number): Promise<RecipeStep[]> {
    return new Promise<RecipeStep[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const requestOptions = new FoodRequestOptions();
        requestOptions.Id_session = cred.Id_Session;
        requestOptions.Id_app = this.Config.AppId;
        requestOptions.ManualJSON = 0;
        requestOptions.Controller0 = 'Recipe';
        requestOptions.EntityId0 = id;
        requestOptions.Section = 'Stage';
        requestOptions.Method = 'Print';
        requestOptions.ApiVersion = 'v1';

        this.restService.get(
          requestOptions).then(x => {
            const response: ServerResponse<RecipeStep> = x;
            resolve(response.Result.Resultset);
          });
      });
    });
  }

  public insertSingleRecipeStep(stageCount: number, stepInfo: string, recipe_id: number): Promise<any> {
    return new Promise<number>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const step = new RecipeStepRequest();
        step.StageOrder = stageCount;
        step.StageText = stepInfo;
        step.ImagePath = 'Null3';
        step.Image = 'null';
        step.Id_session = cred.Id_Session;
        step.Id_app = 3;
        step.Controller0 = 'Recipe';
        step.EntityId0 = recipe_id;
        step.Controller1 = 'Stage';
        step.Method = 'Insert';
        step.ApiVersion = 'v1';

        this.restService.post(step).then(x => {
          if (x !== null) {
            const response: ServerResponse<null> = x;
            resolve(response.Result.ReturnCode);
          }
        });
      });
    });
  }

  public updateSingleRecipeStep(recipeStep: RecipeStepData): Promise<any> {
    return new Promise<any>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const step = new RecipeStepRequest();
        step.Id_Recipe = recipeStep.Id_Recipe;
        step.StageOrder = recipeStep.StageOrder;
        step.StageText = recipeStep.Text;
        step.ImagePath = recipeStep.ImagePath;
        step.Image = 'null';
        step.Id_session = cred.Id_Session;
        step.Id_app = 3;
        step.Controller0 = 'Recipe';
        step.Controller1 = 'Stage';
        step.EntityId1 = recipeStep.Id_Stage;
        step.Method = 'update';
        step.ApiVersion = 'v1';

        this.restService.post(step).then(x => {
          if (x !== null) {
            resolve(x);
          }
        });
      });
    });
  }

  public deleteSingleRecipeStep(id: number) {
    return new Promise<any>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const step = new RecipeStepRequest();
        step.Id_session = cred.Id_Session;
        step.Id_app = 3;
        step.Controller0 = 'Recipe';
        step.Controller1 = 'Stage';
        step.EntityId1 = id;
        step.Method = 'delete';
        step.ApiVersion = 'v1';

        this.restService.post(step).then(x => {
          if (x !== null) {
            resolve(x);
          }
        });
      });
    });
  }

  public updateAllSteps(updatedSteps: RecipeStep[], recipe_id: number, recipeImages: ImageInfo[]): Promise<null> {
    return new Promise(resolve => {
      const promises: Promise<any>[] = [];
      updatedSteps.forEach(element => {
        promises.push(this.updateStep(element.Stage, recipe_id));
      });
      Promise.all(promises).then(x => {
        resolve();
      });
    });
  }

  private checkStepForImage(element: RecipeStepData): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (element.ImageFile) {
        this.filesService.uploadImage(element.ImageFile, EntityType.Stages, element.Id_Stage, null, null).then(response => {
          if (response) {
            element.ImagePath = response;
            element.StateForChange = RecipeComponentStates.Update;
            element.ImageFile = undefined;
          }
          resolve(true);
        }, () => resolve(false));
      } else {
        resolve(false);
      }
    });
  }

  private updateStep(element: RecipeStepData, recipe_id: number): Promise<null> {
    return new Promise<any>(resolve => {
      if (element.StateForChange === RecipeComponentStates.Update) {
        this.checkStepForImage(element).then(res => {
          this.updateSingleRecipeStep(element).then(x => { resolve(); });
        });
      }
      if (element.StateForChange === RecipeComponentStates.Insert) {
        this.insertSingleRecipeStep(element.StageOrder, element.Text, recipe_id).then(x => {
          element.Id_Stage = x;
          element.StateForChange = RecipeComponentStates.Update;
          this.checkStepForImage(element).then(res => {
            if (res) {
              this.updateStep(element, recipe_id).then(z => {
                resolve(null);
              });
            } else {
              resolve(null);
            }
          });
        });
      }
      if (element.StateForChange === RecipeComponentStates.Delete) {
        this.deleteSingleRecipeStep(element.Id_Stage).then(x => { resolve(null); });
      }
      if (element.StateForChange === undefined || element.StateForChange === RecipeComponentStates.Default) {
        resolve(null);
      }
    });
  }

  public insertSingleRecipeComponent(id_newProduct: number, id_curRecipe: number,
    newCalculationWeight: number, newRecalcVolume: number, recipe_id: number) {
    return new Promise<string>((resolve, reject) => {
      this.accountService.getCredentialsPromise().then(cred => {
        const component = new RecipeComponentRequest();
        component.Id_session = cred.Id_Session;
        component.Id_app = 3;
        component.Controller0 = 'Recipe';
        component.EntityId0 = recipe_id;
        component.Controller1 = 'Compaund';
        component.Method = 'Insert';
        component.ApiVersion = 'v1';
        component.IsChangeable = 0;
        component.Id_Product = id_newProduct;
        component.CalculationWeight = newCalculationWeight;

        this.restService.post(component).then(x => {
          if (x.Result.ReturnCode > 0) {
            resolve(x.Result.Message);
            return;
          }
          reject(x);
        });
      });
    });
  }

  public insertNewRecipeComponents(newComponents: RecipeIngredient[], recipe_id: number): Promise<null> {

    return new Promise(resolve => {
      newComponents.forEach(element => {
        if (element.Ingredient.StateForChange === RecipeComponentStates.Insert) {
          this.insertSingleRecipeComponent(element.Ingredient.Id_Product, element.Ingredient.Id_Recipe,
            element.Ingredient.Weight, element.Ingredient.RecalcVolume, recipe_id).then(x => {
              if (x !== null) {
                resolve();
              }
            }, () => {});
        }
      });
    });
  }

  public updateSingleRecipeComponent(idCompaund: number, id_newProduct: number, newProductName: string,
    newCalculationWeight: number, newUIndex: number, recipeId: number) {
    return new Promise<string>((resolve, reject) => {
      this.accountService.getCredentialsPromise().then(cred => {
        const component = new RecipeComponentRequest();
        component.Id_session = cred.Id_Session;
        component.Id_app = 3;
        component.Controller0 = 'Recipe';
        component.Controller1 = 'Compaund';
        component.EntityId1 = idCompaund;
        component.Method = 'update';
        component.ApiVersion = 'v1';
        component.Id_Product = id_newProduct;
        component.ProductFullName = newProductName;
        component.Id_ProductCluster = 501;
        component.UIndex = newUIndex;
        component.CalculationWeight = newCalculationWeight;

        this.restService.post(component).then(x => {
          if (x.Result.ReturnCode > 0) {
            resolve(x.Result.Message);
          }
        });
      });
    });
  }

  public updateAllComponents(updatedComponents: RecipeIngredient[], recipe_id: number): Promise<null> {
    return new Promise(resolve => {
      const promises: Promise<any>[] = [];
      updatedComponents.forEach(element => {
        switch (element.Ingredient.StateForChange) {
          case RecipeComponentStates.Update:
            promises.push(this.updateSingleRecipeComponent(element.Ingredient.Id_Compaund, element.Ingredient.Id_Product,
              element.Ingredient.Name, element.Ingredient.Weight, element.Ingredient.UIndex, recipe_id));
            return;
          case RecipeComponentStates.Delete:
            if (element.Ingredient.Id_Compaund !== undefined) {
              promises.push(this.deleteSingleRecipeComponentPromise(element.Ingredient.Id_Compaund));
            }
            return;
          case RecipeComponentStates.Insert:
            if (recipe_id) {
              promises.push(this.insertSingleRecipeComponent(element.Ingredient.Id_Product, recipe_id,
                element.Ingredient.Weight, element.Ingredient.RecalcVolume, recipe_id));
            }
            return;
          default:
            return;
        }
      });
      Promise.all(promises).then(() => resolve(), () => resolve());
    });
  }

  public deleteSingleRecipeComponent(compaundId: number) {
    if (!compaundId) {
      return;
    }
    this.accountService.getCredentialsPromise().then(cred => {
      const component = new RecipeComponentRequest();
      component.Id_session = cred.Id_Session;
      component.Id_app = 3;
      component.Controller0 = 'Recipe';
      component.Controller1 = 'Compaund';
      component.EntityId1 = compaundId;
      component.Method = 'delete';
      component.ApiVersion = 'v1';

      this.restService.post(component).then(x => this.insertStageSubject.next(x.Result.Message));
    });
    return this.insertStageSubject.asObservable();
  }

  public deleteSingleRecipeComponentPromise(compaundId: number): Promise<any> {
    return new Promise<string>((resolve, reject) => {

      this.accountService.getCredentialsPromise().then(cred => {
        const component = new RecipeComponentRequest();
        component.Id_session = cred.Id_Session;
        component.Id_app = 3;
        component.Controller0 = 'Recipe';
        component.Controller1 = 'Compaund';
        component.EntityId1 = compaundId;
        component.Method = 'delete';
        component.ApiVersion = 'v1';

        this.restService.post(component).then(x => {
          if (x.Result.ReturnCode > 0) {
            resolve(x.Result.Message);
          }
        });
      });

    });
  }

  public async setFilter(name: string, value, forceUpdate?: boolean): Promise<boolean> {
    if (this.requestOptions[name] !== value || forceUpdate) {
      this.numberOfPages = null;
      this.numberOfPagesSubject.next(this.numberOfPages);
      this.numberOfShortPages = null;
      this.numberOfShortPagesSubject.next(this.numberOfShortPages);
      this.requestOptions.Page = 1;
      this.shortRequestOptions.Page = 1;
      this.isFilterChanged = true;
      this.isShortFilterChanged = true;
      this.requestOptions[name] = value;
      this.shortRequestOptions[name] = value;
    }
    return true;
  }

  public increasePage(): void {
    if (this.requestOptions.Page < this.numberOfPages) {
      this.requestOptions.Page++;
      this.currentPageSubject.next(this.requestOptions.Page);

      this.restService.get(
        this.requestOptions).then(x => {
          const response: ServerResponse<Recipe> = x;

          this.recipes = this.recipes.concat(response.Result.Resultset);
          this.recipesSubject.next(this.recipes);
        });
    }
  }

  public toggleRecipeFavorite(recipe: Recipe) {
    return new Promise<any>(
      resolve => {
        this.accountService.getCredentialsPromise().then(cred => {
          const recipeRequest = new RecipeFilter();
          recipeRequest.Id_session = cred.Id_Session;
          recipeRequest.Id_app = 3;
          recipeRequest.Controller0 = 'User';
          recipeRequest.EntityId0 = cred.Id_User;
          recipeRequest.Controller1 = 'Recipe';
          recipeRequest.EntityId1 = recipe.Id_Recipe;
          recipeRequest.Method = recipe.IsFavorite ? 'ClearFavorit' : 'SetFavorit';
          recipeRequest.ApiVersion = 'v1';
          recipeRequest.ManualJSON = 0;
          this.restService.post(recipeRequest).then(x => {
            if (x) {
              const response: ServerResponse<any> = x;
              if (response.Result.ReturnCode > 0) {
                resolve(response);
              }
              return;
            }
          });
        });
      });
  }

  public toggleRecipeExcuded(recipe: Recipe) {
    return new Promise<any>(
      resolve => {
        this.accountService.getCredentialsPromise().then(cred => {
          const recipeRequest = new RecipeFilter();
          recipeRequest.Id_session = cred.Id_Session;
          recipeRequest.Id_app = 3;
          recipeRequest.Controller0 = 'User';
          recipeRequest.EntityId0 = cred.Id_User;
          recipeRequest.Controller1 = 'Recipe';
          recipeRequest.EntityId1 = recipe.Id_Recipe;
          recipeRequest.Method = recipe.IsXcluded ? 'ClearExcuded' : 'SetExcuded';
          recipeRequest.ApiVersion = 'v1';
          recipeRequest.ManualJSON = 0;
          this.restService.post(recipeRequest).then(x => {
            if (x) {
              const response: ServerResponse<any> = x;
              if (response.Result.ReturnCode > 0) {
                this.refresh();
                this.refreshShort();
                resolve(response);
              }
              return;
            }
          });
        });
      });
  }

  public getModalAddToMenuState(): Observable<AddToMenuShowModalObj> {
    return this.showModalAddToMenu;
  }

  public openModalAddToMenu(
    e: MouseEvent,
    recipeId: number,
    stageId: number,
    calculationWeight: number,
    recipeChanged: number,
    portion: number,
    typeMenu: number,
    singleNPart: number
  ): void {
    let isMinified = false;
    if (typeof recipeChanged === 'number') {
      isMinified = recipeChanged !== 0;
    }
    e.stopPropagation();
    e.preventDefault();

    this.showModalAddToMenu.next({
      recipeId: recipeId,
      stageId: stageId ? stageId : 0,
      calculationWeight: calculationWeight,
      isModified: isMinified,
      portion: portion ? portion : 0,
      typeMenu: typeMenu,
      singleNPart: singleNPart
    });
  }

  public closeModalAddToMenu(): void {
    this.showModalAddToMenu.next({
      recipeId: 0,
      stageId: 0,
      calculationWeight: 0.,
      isModified: false,
      portion: 0,
      typeMenu: 0,
      singleNPart: 0,
    });
  }

  public getQueryParams(): Observable<boolean> {
    const queryParamsObj = this.route.snapshot.queryParams;
    if (Object.keys(queryParamsObj).length === 0) {
      if (!this.isHaveValueInHash()) {
        this.pushProgressInHash('noHash');
      }
      return this.hashFiltered.asObservable();
    }

    this.createQueryMap(queryParamsObj);
    window.history.pushState({}, document.title, '/main/recipes/list');

    if (this.queryParamsMap.has('sort')) {
      const sortArr = decodeURI(this.queryParamsMap.get('sort')).split(' ');
      const sort = sortArr[0];
      const direction = sortArr[1] ? sortArr[1] === 'desc' ? sortArr[1] : 'asc' : 'asc';
      this.switchQuerySort(sort, direction);
    } else {
      this.setFilter('IsExcluded', '0');
    }

    if (this.queryParamsMap.has('ingredients')) {
      this.productsIdHash = this.queryParamsMap.get('ingredients').split(',');
      this.sortHashObs.next(2);
    } else {
      this.pushProgressInHash('ingredients');
    }

    if (this.queryParamsMap.has('books')) {
      this.booksIdHash = this.queryParamsMap.get('books').split(',');
      this.sortHashObs.next(3);
    } else {
      this.pushProgressInHash('books');
    }

    if (this.queryParamsMap.has('category')) {
      this.categoryNameHash = this.queryParamsMap.get('recipegroup') ? this.queryParamsMap.get('recipegroup') :
        this.queryParamsMap.get('subcategory') ? this.queryParamsMap.get('subcategory') :
          this.queryParamsMap.get('category');
      // исключение повторных вариантов проверкой категории
      this.categoryParentNameHash = this.queryParamsMap.get('category');
      this.subCategoryNameHash = this.queryParamsMap.get('subcategory');
      this.categoryTypeHash = this.queryParamsMap.get('recipegroup') ? 3 :
        this.queryParamsMap.get('subcategory') ? 2 :
          this.queryParamsMap.get('category') ? 1 : 0;
      this.sortHashObs.next(4);
    } else {
      this.pushProgressInHash('category');
    }

    if (this.queryParamsMap.has('search')) {
      this.searchItem = this.queryParamsMap.get('search');
      this.sortHashObs.next(5);
    } else {
      this.pushProgressInHash('search');
    }

    return this.hashFiltered.asObservable();
  }

  public pushProgressInHash(str: string) {
    this.progressHash.push(str);
    this.hashFiltered.next(true);
  }

  public isHaveValueInHash() {
    return this.progressHash.some(str => str === 'noHash');
  }

  public lengthProgressHash() {
    return this.progressHash.length;
  }

  private createQueryMap(queryParamsObj: object): void {
    const queryMap = new Map();
    for (const key of Object.keys(queryParamsObj)) {
      queryMap.set(key.toLowerCase(), decodeURI(queryParamsObj[key]));
    }
    this.queryParamsMap = queryMap;
  }

  private switchQuerySort(sort: string, dir: string): void {
    let flag = false;

    this.orderFields.forEach( field => {
      if (sort.toLowerCase() === field.value.toLowerCase()) {
        this.setFilter('Sort', `${field.value} ${dir}`);
        this.selectedField = {
          label: field.label,
          value: field.value
        };
        this.sortHashDirection = dir !== 'desc';
        flag = true;
        this.sortHashObs.next(1);
      }
    });

    // установка дефолтного если существует и не подходит
    if (!flag) {
      this.setFilter('IsExcluded', '0');
    }
  }

  private refreshShotRecipes(): void {
    if (this.shortRequestOptions && !this.isShortLoading) {
      this.isShortLoading = true;
      this.restService.get(this.shortRequestOptions).then(y => {
          if (y !== null) {
            this.isShortLoading = false;
            const response: ServerResponse<Recipe> = y;
            if (this.numberOfShortPages == null) {
              this.numberOfShortPages = response.Result.ReturnCode;
              this.numberOfShortPagesSubject.next(this.numberOfShortPages);
            }

            this.shortRecipes = response.Result.Resultset;
            this.shortRecipesSubject.next(this.shortRecipes);
          }
        });
    }
  }

  private refreshRecipes(): any {
    if (this.requestOptions.Id_session && !this.isLoading) {
      this.isLoading = true;
      this.restService.get(
        this.requestOptions).then(y => {
          if (y !== null) {
            this.isLoading = false;
            const response: ServerResponse<Recipe> = y;
            if (this.numberOfPages == null) {
              this.numberOfPages = response.Result.ReturnCode;
              this.numberOfPages = response.Result.ReturnCode;
              this.numberOfPagesSubject.next(this.numberOfPages);
            }

            this.recipes = response.Result.Resultset;
            this.recipesSubject.next(this.recipes);
          }
        });
      this.requestOptions.PageSize = 10;
    }
  }

  getFilterState(): Observable<number> {
    return this.clearFilterState.asObservable();
  }

  clearFilters() {
    this.requestOptions = new RecipeFilter();
    this.isFilterChanged = true;
    this.numberOfPages = null;
    this.refresh();
    this.shortRequestOptions = new RecipeFilter();
    this.isShortFilterChanged = true;
    this.numberOfShortPages = null;
    this.refreshShort();
    this.clearFilterState.next(1);
  }

  clearFiltersForEdit() {
    this.requestOptions = new RecipeFilter();
    this.isFilterChanged = true;
    this.numberOfPages = null;
    this.clearFilterState.next(1);
  }
  clearData() {
    this.recipes = null;
    this.recipesSubject.next(this.recipes);
    this.shortRecipes = null;
    this.shortRecipesSubject.next(this.shortRecipes);
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/recipe.service.ts